/**
 * How a work item is referred to in the UI.
 *
 * `key` (`PO46-828`) is the canonical, estate-unique, immutable reference.
 * `displayId` (`ST-3`) is a LOCAL badge and is NOT unique: features, stories and
 * tasks are numbered per PARENT, so `ST-3` means "the third story under its
 * feature". Measured on the live estate before keys existed: 10,529 of 12,829
 * visible items shared a displayId with a sibling in the SAME project, `ST-1`
 * named 2,066 stories, and `PO-1` named 13 different projects.
 *
 * So anything a person might quote, search for or paste into a comment must
 * render the key. The badge stays useful inside a tree, where position is
 * already implied by what it is nested under.
 */

export interface Referenceable {
  key?: string;
  displayId?: string;
}

/**
 * The reference to show. Falls back to `displayId` only for items created
 * before the backfill — every item has a key now, and the fallback exists so a
 * stale cached response degrades to the old ambiguous badge rather than to a
 * blank space.
 */
export function itemRef(item: Referenceable | null | undefined): string {
  if (!item) return '';
  return item.key || item.displayId || '';
}

/** True when the reference shown is the ambiguous legacy badge. */
export function isLegacyRef(item: Referenceable | null | undefined): boolean {
  return Boolean(item && !item.key && item.displayId);
}
